import { Schema } from "effect";

/**
 * Independently configurable patient-information categories.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoPolicyFields } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * Schema.decodeUnknownSync(PatientInfoPolicyFields)({
 *   phone: false,
 *   email: true,
 *   hausarzt: true,
 * });
 * ```
 */
export const PatientInfoPolicyFields = Schema.Struct({
  phone: Schema.Boolean,
  email: Schema.Boolean,
  hausarzt: Schema.Boolean,
});

/**
 * The decoded type of {@link PatientInfoPolicyFields}.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import type { PatientInfoPolicyFields } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const fields: PatientInfoPolicyFields = { phone: true, email: true, hausarzt: false };
 * ```
 */
export type PatientInfoPolicyFields = typeof PatientInfoPolicyFields.Type;

/**
 * Field selection used before a practice has customised the review.
 *
 * All categories remain selected so enabling an older or never-stored policy preserves the complete
 * review that existed before granular settings were introduced.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import { DEFAULT_PATIENT_INFO_POLICY_FIELDS } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * DEFAULT_PATIENT_INFO_POLICY_FIELDS.phone; // true
 * ```
 */
export const DEFAULT_PATIENT_INFO_POLICY_FIELDS: PatientInfoPolicyFields = {
  phone: true,
  email: true,
  hausarzt: true,
};

/**
 * Whether at least one review category is selected.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import { hasEnabledPatientInfoField } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * hasEnabledPatientInfoField({ phone: false, email: true, hausarzt: false }); // true
 * ```
 */
export function hasEnabledPatientInfoField(
  fields: PatientInfoPolicyFields,
): boolean {
  return fields.phone || fields.email || fields.hausarzt;
}

/**
 * Whether the policy store could be consulted at all.
 *
 * `unavailable` is not a synonym for disabled: the practice must be able to tell "we turned this off"
 * from "we cannot currently tell", because the second is a fault to investigate rather than a
 * setting. Both nevertheless leave the feature off — unavailability is never permission.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PolicyAvailability } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * Schema.decodeUnknownSync(PolicyAvailability)("available");
 * ```
 */
export const PolicyAvailability = Schema.Literals(["available", "unavailable"]);

/**
 * The decoded type of a {@link PolicyAvailability}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PolicyAvailability } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const availability: PolicyAvailability = "available";
 * ```
 */
export type PolicyAvailability = typeof PolicyAvailability.Type;

/**
 * The practice-wide patient-info policy as the admin UI sees it.
 *
 * `version` is what an update must echo back, so two administrators cannot silently overwrite each
 * other. Version 0 means "no policy stored yet" and is the correct expected version for the first
 * enable.
 *
 * Deliberately carries no practice identifier, actor, or persistence detail: the server owns all
 * three, and putting any of them on the wire would invite a client to select them.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoPolicyView } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const view = Schema.decodeUnknownSync(PatientInfoPolicyView)({
 *   enabled: false,
 *   fields: { phone: true, email: true, hausarzt: true },
 *   version: 0,
 *   availability: "available",
 * });
 * ```
 */
export const PatientInfoPolicyView = Schema.Struct({
  enabled: Schema.Boolean,
  fields: PatientInfoPolicyFields,
  version: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  availability: PolicyAvailability,
  updatedAt: Schema.optional(Schema.DateTimeUtcFromString),
});

/**
 * The decoded type of a {@link PatientInfoPolicyView}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoPolicyView } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * function canEnable(view: PatientInfoPolicyView): boolean {
 *   return view.availability === "available";
 * }
 * ```
 */
export type PatientInfoPolicyView = typeof PatientInfoPolicyView.Type;

/**
 * An administrator's request to change the policy.
 *
 * Carries the desired master state, independently selected review categories, and the version the
 * administrator believed they were changing. It deliberately carries no practice, tenant, or actor:
 * the server establishes all of those from its deployment and authenticated session, so a browser
 * cannot nominate them.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoPolicyUpdate } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const update = Schema.decodeUnknownSync(PatientInfoPolicyUpdate)({
 *   enabled: true,
 *   fields: { phone: false, email: true, hausarzt: true },
 *   expectedVersion: 0,
 * });
 * ```
 */
export const PatientInfoPolicyUpdate = Schema.Struct({
  enabled: Schema.Boolean,
  fields: PatientInfoPolicyFields,
  expectedVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
}).pipe(
  Schema.check(Schema.makeFilter((update) => !update.enabled || hasEnabledPatientInfoField(update.fields), { message: "At least one patient-information field must be enabled" })),
);

/**
 * The decoded type of a {@link PatientInfoPolicyUpdate}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoPolicyUpdate } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const update: PatientInfoPolicyUpdate = {
 *   enabled: true,
 *   fields: { phone: true, email: false, hausarzt: true },
 *   expectedVersion: 1,
 * };
 * ```
 */
export type PatientInfoPolicyUpdate = typeof PatientInfoPolicyUpdate.Type;

/**
 * Result returned by the SOL-authenticated settings RPC after a policy save attempt.
 *
 * Conflicts and store unavailability are ordinary typed outcomes so the client can show them without
 * inspecting an RPC transport failure.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoPolicySaveResponse } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * Schema.decodeUnknownSync(PatientInfoPolicySaveResponse)({ outcome: "unavailable" });
 * ```
 */
export const PatientInfoPolicySaveResponse = Schema.Union(
  [Schema.Struct({
    outcome: Schema.Literal("saved"),
    view: PatientInfoPolicyView,
  }),
  Schema.Struct({
    outcome: Schema.Literal("version-conflict"),
    current: PatientInfoPolicyView,
  }),
  Schema.Struct({ outcome: Schema.Literal("unavailable") })],
);

/**
 * The decoded type of {@link PatientInfoPolicySaveResponse}.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * import type { PatientInfoPolicySaveResponse } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * const result: PatientInfoPolicySaveResponse = { outcome: "unavailable" };
 * ```
 */
export type PatientInfoPolicySaveResponse =
  typeof PatientInfoPolicySaveResponse.Type;

/**
 * A safe, typed error body for the admin policy routes.
 *
 * Modelled on RFC 7807 problem details, deliberately narrow: a coarse `problem` code and a
 * human-readable title, never a thrown object, a MongoDB error, the raw request body, or an internal
 * identifier.
 *
 * `version-conflict` carries the current view so the UI can show what it actually is now instead of
 * making the admin guess.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoPolicyProblem } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * Schema.decodeUnknownSync(PatientInfoPolicyProblem)({
 *   problem: "unavailable",
 *   title: "Die Einstellung ist derzeit nicht verfügbar.",
 * });
 * ```
 */
export const PatientInfoPolicyProblem = Schema.Struct({
  problem: Schema.Literals(
    ["malformed-request",
    "unauthenticated",
    "unavailable",
    "version-conflict"],
  ),
  title: Schema.String,
  current: Schema.optional(PatientInfoPolicyView),
});

/**
 * The decoded type of a {@link PatientInfoPolicyProblem}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoPolicyProblem } from "@ascleo/frontdesk-contracts/patient-info/policy";
 *
 * function isConflict(problem: PatientInfoPolicyProblem): boolean {
 *   return problem.problem === "version-conflict";
 * }
 * ```
 */
export type PatientInfoPolicyProblem = typeof PatientInfoPolicyProblem.Type;
